import PropTypes from 'prop-types'
import { iso8601PropType } from '../common/iso-8601-prop-type'
import { bookedFeaturePropType } from './booked-feature-prop-type'
import { devicePropType } from './device-prop-type'

const CONTRACT_TYPES = {
    RENTAL: 'RENTAL',
    SALES: 'SALES'
}

/**
 * @typedef {Object} subscriptionPropType
 *
 * @property {string}                       [accountName]                                   - The account name the subscription belongs to
 * @property {string}                       [accountNo]                                     - The account number the subscription belongs to
 * @property {array<bookedFeaturePropType>} [additionalFeatures]                            - List of additional features on this subscription
 * @property {string}                       [agreementNo]                                   - The agreement number the subscription belongs to
 * @property {string}                       [commissionBundleId]                            - The identifier of the commission bundle of the subscription
 * @property {number}                       [contractDuration]                              - The duration of the contract
 * @property {string}                       [contractId]                                    - The identifier of the contract (WfBillContract)
 * @property {string}                       [contractNo]                                    - The number of the contract
 * @property {string}                       [contractType]                                  - The type of contract
 * @property {string}                       [customerCurrency]                              - The currency of the customer (e.g. 'EUR')
 * @property {string}                       [customerName]                                  - The name of the customer
 * @property {string}                       [customerNo]                                    - The number of the customer
 * @property {array<devicePropType>}        [devices]                                       - The List of associated devices
 * @property {string}                       [deviceSerialNo]                                - The serial number of the device
 * @property {boolean}                      [hasRecurringBookingsWithSpreadPayment]         - Flag to indicate if the subscription has recurring bookings where the price is spread over the monthly payments during the minimum contract duration.
 * @property {string}                       [id]                                            - The identifier of the subscription (WfBillContract)
 * @property {string}                       [invoiceReference]                              - The reference to the invoice
 * @property {boolean}                      [isHidden]                                      - The hidden property
 * @property {string}                       [numberPlate]                                   - The number plate of the vehicle
 * @property {string}                       [obdDeviceIdent]                                - The serial number of the connected OBD device
 * @property {string}                       [obdDeviceType]                                 - The device type of the connected OBD
 * @property {string}                       [objectId]                                      - The identifier of the vehicle
 *                                                                                            Can be null for not activated contracts. (no WfUnit+ WfObject)
 * @property {string}                       [objectName]                                    - The name of the vehicle
 * @property {string}                       [objectNo]                                      - The number of the vehicle (WfObject)
 * @property {string}                       [pndMuid]                                       - The serial number of the connected PND
 * @property {string}                       [pndType]                                       - The type of the connected PND
 * @property {number}                       [price]                                         - The monthly price of the subscription
 * @property {string}                       [prolongationDate]                              - The prolongation date
 * @property {string}                       [simNo]                                         - The SIM number of the subscription
 * @property {number}                       [smartBundleId]                                 - The Id of smart bundle
 * @property {string}                       [smartBundleName]                               - The name of the smart bundle
 * @property {string}                       [smartBundleNameStringCode]                     - The string code of the smart bundle name
 * @property {string}                       [statusStringcode]                              - The string code for the status of the device
 * @property {string}                       [tariffGroupId]                                 - The identifier for the tariff group of the subscription
 * @property {string}                       [tariffId]                                      - The identifier for the tariff of the subscription
 * @property {string}                       [tariffName]                                    - The name of the booked tariff of the subscription
 * @property {string}                       [tariffServiceId]                               - The identifier of the tariff service
 * @property {string}                       [terminationDate]                               - The date the subscriptions is currently valid to
 * @property {string}                       [unitType]                                      - The technical code for the unit type
 * @property {string}                       [unitTypeTranslated]                            - The translated unit type
 * @property {string}                       [validFrom]                                     - The date the subscription is valid from
 * @property {iso8601}                      [validToForRecurringBookingsWithSpreadPayment]  - The furthest valid to date of the among the recurring bookings with spread payment.
 * @property {string}                       [vin]                                           - The Vehicle Ident Number of the vehicle
 */
export const subscriptionPropType = PropTypes.shape({
    accountName: PropTypes.string,
    accountNo: PropTypes.string,
    additionalFeatures: PropTypes.arrayOf(bookedFeaturePropType),
    agreementNo: PropTypes.string,
    commissionBundleId: PropTypes.string,
    contractDuration: PropTypes.number,
    contractId: PropTypes.string,
    contractNo: PropTypes.string,
    contractType: PropTypes.oneOf(Object.values(CONTRACT_TYPES)),
    customerCurrency: PropTypes.string,
    customerName: PropTypes.string,
    customerNo: PropTypes.string,
    deviceSerialNo: PropTypes.string,
    devices: PropTypes.arrayOf(devicePropType),
    hasRecurringBookingsWithSpreadPayment: PropTypes.bool,
    id: PropTypes.string,
    invoiceReference: PropTypes.string,
    isHidden: PropTypes.bool,
    numberPlate: PropTypes.string,
    obdDeviceIdent: PropTypes.string,
    obdDeviceType: PropTypes.string,
    objectId: PropTypes.string,
    objectName: PropTypes.string,
    objectNo: PropTypes.string,
    pndMuid: PropTypes.string,
    pndType: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.number, PropTypes.arrayOf(PropTypes.object)]),
    prolongationDate: PropTypes.string,
    simNo: PropTypes.string,
    smartBundleId: PropTypes.number,
    smartBundleName: PropTypes.string,
    smartBundleNameStringCode: PropTypes.string,
    statusStringcode: PropTypes.string,
    tariffGroupId: PropTypes.string,
    tariffId: PropTypes.string,
    tariffName: PropTypes.string,
    tariffServiceId: PropTypes.string,
    terminationDate: PropTypes.string,
    unitType: PropTypes.string,
    unitTypeTranslated: PropTypes.string,
    validFrom: PropTypes.string,
    validToForRecurringBookingsWithSpreadPayment: iso8601PropType,
    vin: PropTypes.string
})
